import { NextSeo } from "next-seo";

import type { FeatureContent } from "@/lib/features/content";
import FeatureHero from "@/components/features/FeatureHero";
import FeatureSteps from "@/components/features/FeatureSteps";
import EditorDemoSection from "@/components/home/EditorDemoSection";
import FeaturesBento from "@/components/home/FeaturesSection";
import Testimonials from "@/components/home/Testimonials";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";
import { HUBS } from "@/lib/seo/pages";
import { getLocalizedHome, localizedHref } from "@/lib/i18n/content";
import { useStrings } from "@/lib/i18n/context";
import { LOCALE_META, localizedPath, type Locale } from "@/lib/i18n/locales";
import { localeSeoProps } from "@/lib/i18n/seo";

type FeaturePageBodyProps = {
  content: FeatureContent;
  locale: Locale;
  path: string;
};

const FeaturePageBody = ({ content, locale, path }: FeaturePageBodyProps) => {
  const s = useStrings();
  const localPath = localizedPath(locale, path);
  const crumbs =
    locale === "en"
      ? breadcrumbsForPath(path)
      : [
          { name: s.breadcrumbs.home, href: localizedHref(locale, "/") },
          { name: s.breadcrumbs.features, href: HUBS.feature.path },
          { name: content.title, href: localPath },
        ];
  const faqs = content.faqs ?? getLocalizedHome(locale).faqs;

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white transition-all duration-200 ease-in-out">
      <NextSeo
        title={`${content.seoTitle} | Crayo`}
        description={content.seoDescription}
        {...localeSeoProps(locale, path)}
      />
      <JsonLd
        nodes={pageGraph({
          path: localPath,
          name: content.seoTitle,
          description: content.seoDescription,
          crumbs,
          faqs,
          speakable: [".answer-first"],
          ...(locale === "en" ? {} : { inLanguage: LOCALE_META[locale].htmlLang }),
        })}
      />
      <Breadcrumbs items={crumbs} />
      <FeatureHero featureContent={content} />
      <FeatureSteps featureContent={content} />
      <EditorDemoSection />
      <FeaturesBento />
      <Testimonials />
      <RelatedPages path={path} header={s.related.header} />
      <FAQ items={faqs} />
      <Footer />
    </div>
  );
};

export default FeaturePageBody;
